import { featureSchema } from "@stb/api-contract"
import { z } from "zod"

/** Integer minor units (cents). Money rule: never a float; the currency is `PRODUCT_CONFIG.currency`. */
const cents = z.number().int().nonnegative()
/** A count per month: `null` = unlimited, 0 = none. */
const quota = z.number().int().nonnegative().nullable()
const positiveInt = z.number().int().positive()

const planLimits = z.object({
  tripsMonthly: quota,
  aiReadsMonthly: quota,
  invoicesMonthly: quota,
  assistantTurnsMonthly: quota,
  vehicles: positiveInt.max(50),
  features: z.array(featureSchema).readonly(),
})

const paidPlan = planLimits.extend({ monthlyCents: cents, yearlyCents: cents })

/**
 * Plan ids stay `free | logbook | pro`: the API contract enum (`me.ts` plan) is strict, so a
 * fourth plan is never a config-only change.
 */
const yearlyTooHigh = (monthly: number, yearly: number) => yearly > monthly * 12

export const productConfigSchema = z
  .object({
    currency: z.literal("AUD"),
    /** Prices still carry the Draft badge when true (D-PRICE-KEEP). */
    pricesDraft: z.boolean(),
    plans: z.object({
      free: planLimits.extend({ monthlyCents: z.literal(0) }),
      logbook: paidPlan,
      pro: paidPlan,
    }),
    addons: z.object({
      /** A feature add-on grants the quotas of the named plan for that feature. */
      receipts: z.object({ monthlyCents: cents, yearlyCents: cents, grants: z.literal("pro") }),
      invoicing: z.object({ monthlyCents: cents, yearlyCents: cents, grants: z.literal("pro") }),
      vehicle: z.object({ monthlyCents: cents, maxQuantity: positiveInt }),
    }),
    team: z.object({
      includedSeats: positiveInt,
      maxSeats: positiveInt,
      baseMonthlyCents: cents,
      baseYearlyCents: cents,
      extraSeatMonthlyCents: cents,
      extraSeatYearlyCents: cents,
    }),
    /** One card-free trial length, used by the Pro trial and the Team plan trial. */
    trial: z.object({ days: z.number().int().min(1).max(90), plan: z.literal("pro") }),
    freeTier: z.object({
      /** Server upload cap = free trips quota + this many extra masked trips (anti-abuse). */
      maskedTripsAfterQuota: z.number().int().nonnegative(),
    }),
    introOffer: z.object({
      plan: z.enum(["logbook", "pro"]),
      billing: z.enum(["monthly", "yearly"]),
      endsOn: z.iso.date().nullable(),
    }),
    webCheckoutPlans: z.array(z.enum(["logbook", "pro"])).readonly(),
    stripe: z.object({
      /** Price ids live in wrangler vars; only the key names are product configuration. */
      priceKeys: z.array(z.string().min(1)).readonly(),
    }),
  })
  .superRefine((c, ctx) => {
    const fail = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: "custom", path, message })
    for (const id of ["logbook", "pro"] as const) {
      const p = c.plans[id]
      if (yearlyTooHigh(p.monthlyCents, p.yearlyCents))
        fail(["plans", id, "yearlyCents"], "yearly price above 12 x monthly")
    }
    for (const id of ["receipts", "invoicing"] as const) {
      const a = c.addons[id]
      if (yearlyTooHigh(a.monthlyCents, a.yearlyCents))
        fail(["addons", id, "yearlyCents"], "yearly price above 12 x monthly")
    }
    const t = c.team
    if (yearlyTooHigh(t.baseMonthlyCents, t.baseYearlyCents))
      fail(["team", "baseYearlyCents"], "yearly price above 12 x monthly")
    if (yearlyTooHigh(t.extraSeatMonthlyCents, t.extraSeatYearlyCents))
      fail(["team", "extraSeatYearlyCents"], "yearly price above 12 x monthly")
    if (t.includedSeats > t.maxSeats)
      fail(["team", "includedSeats"], "includedSeats above maxSeats")
    for (const id of ["free", "logbook", "pro"] as const) {
      const f = c.plans[id].features
      if (new Set(f).size !== f.length) fail(["plans", id, "features"], "duplicate feature id")
    }
    if (c.plans.free.tripsMonthly === null)
      fail(
        ["plans", "free", "tripsMonthly"],
        "free trips must be capped (the upload cap derives from it)",
      )
    if (new Set(c.webCheckoutPlans).size !== c.webCheckoutPlans.length)
      fail(["webCheckoutPlans"], "duplicate plan")
    if (!c.webCheckoutPlans.includes(c.introOffer.plan))
      fail(["introOffer", "plan"], "intro offer plan is not sold on the web")
    if (new Set(c.stripe.priceKeys).size !== c.stripe.priceKeys.length)
      fail(["stripe", "priceKeys"], "duplicate price key")
  })

export type ProductConfigInput = z.input<typeof productConfigSchema>
